import React, { useEffect, useState } from "react";
import { Cable, Camera, MonitorUp, Plus, RadioTower, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "../../components/ui/sonner";
import { useStudioAuth } from "../StudioAuthContext";
import { EmptyState, FieldLabel, OPS_INPUT, Panel, StatusChip } from "../ui";

const types = [
  { key: "hls", label: "HLS / Remote", icon: RadioTower },
  { key: "youtube", label: "YouTube", icon: MonitorUp },
  { key: "camera", label: "Camera", icon: Camera },
  { key: "browser", label: "Browser", icon: Cable },
];

export const SourcesSection = () => {
  const { request } = useStudioAuth();
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({ title: "", kind: "hls", src: "", location: "", chaser: "", live: false });

  const load = async () => {
    const response = await request("/streams");
    if (response.ok) setSources(await response.json());
  };
  useEffect(() => { load(); }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (event) => {
    event.preventDefault();
    const response = await request("/streams", { method: "POST", body: JSON.stringify(form) });
    if (!response.ok) return toast.error("Source could not be added");
    setForm({ title: "", kind: "hls", src: "", location: "", chaser: "", live: false });
    await load();
    toast.success("Source added");
  };

  const toggle = async (source) => {
    const response = await request(`/streams/${source.id}`, { method: "PATCH", body: JSON.stringify({ live: !source.live }) });
    if (!response.ok) return toast.error("Source could not be updated");
    await load();
    toast.success(source.live ? "Source disabled — removed from the public site" : "Source enabled — now public");
  };

  const remove = async (sourceId) => {
    const response = await request(`/streams/${sourceId}`, { method: "DELETE" });
    if (response.ok) setSources((items) => items.filter((item) => item.id !== sourceId));
  };

  return (
    <div className="grid gap-3 xl:grid-cols-[1fr_320px]">
      <Panel title="Connected Sources" icon={Cable} note={`${sources.filter((item) => item.live).length} enabled`} bodyClass="p-0">
        {sources.length === 0 ? (
          <EmptyState icon={Cable} title="No media sources configured" message="Add a real source URL to make it available to scenes and the public site." testId="sources-empty-state" />
        ) : (
          <div className="divide-y divide-[rgba(120,170,255,0.12)]">
            {sources.map((source) => (
              <div key={source.id} data-testid={`source-${source.id}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${source.live ? "bg-emerald-400" : "bg-slate-600"}`} />
                    <span className="truncate text-sm font-bold text-white">{source.title}</span>
                  </div>
                  <div className="mono mt-1 truncate text-[9px] uppercase tracking-widest text-slate-600">{source.kind} · {source.location || "No location"}</div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button data-testid={`toggle-source-${source.id}`} onClick={() => toggle(source)}
                    className={`mono h-8 border px-2.5 text-[9px] font-bold uppercase tracking-widest transition-colors ${source.live ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-white/10 text-slate-500 hover:text-slate-200"}`}>
                    {source.live ? "Enabled" : "Disabled"}
                  </button>
                  <button data-testid={`delete-source-${source.id}`} onClick={() => remove(source.id)} title="Delete source" className="grid h-8 w-8 place-items-center border border-white/10 text-slate-500 transition-colors hover:border-red-500/30 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Add Source" icon={Plus} className="h-fit" bodyClass="p-4">
        <form onSubmit={create} className="space-y-3">
          <div className="space-y-1">
            <FieldLabel>Source name</FieldLabel>
            <Input data-testid="source-title-input" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="Chaser 1" required className={OPS_INPUT} />
          </div>
          <div className="space-y-1.5">
            <FieldLabel>Type</FieldLabel>
            <div className="grid grid-cols-2 gap-1">
              {types.map(({ key, label, icon: Icon }) => (
                <button type="button" key={key} data-testid={`source-kind-${key}`} onClick={() => setForm({ ...form, kind: key })}
                  className={`flex h-8 items-center gap-1.5 border px-2 text-[10px] font-bold uppercase tracking-wider transition-colors ${form.kind === key ? "border-sky-500 bg-sky-500/15 text-white" : "border-white/10 text-slate-500 hover:text-slate-200"}`}>
                  <Icon className="h-3.5 w-3.5" />{label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <FieldLabel>Stream URL</FieldLabel>
            <Input data-testid="source-url-input" value={form.src} onChange={(event) => setForm({ ...form, src: event.target.value })} placeholder="https://…" required className={`${OPS_INPUT} mono text-xs`} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <FieldLabel>Location</FieldLabel>
              <Input data-testid="source-location-input" value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value })} placeholder="Norman, OK" className={OPS_INPUT} />
            </div>
            <div className="space-y-1">
              <FieldLabel>Operator</FieldLabel>
              <Input data-testid="source-operator-input" value={form.chaser} onChange={(event) => setForm({ ...form, chaser: event.target.value })} placeholder="Name" className={OPS_INPUT} />
            </div>
          </div>
          <div className="flex items-center justify-between border border-white/10 bg-white/[0.02] px-3 py-2">
            <span className="mono text-[9px] uppercase tracking-widest text-slate-500">Publish immediately</span>
            <button type="button" data-testid="source-live-toggle" onClick={() => setForm({ ...form, live: !form.live })}>
              <StatusChip state={form.live ? "ok" : "idle"}>{form.live ? "Public" : "Private"}</StatusChip>
            </button>
          </div>
          <Button data-testid="add-source-button" type="submit" className="h-9 w-full rounded-none bg-[#1a5cff] text-[11px] font-bold uppercase tracking-widest hover:bg-[#2a6cff]">
            <Plus className="h-4 w-4" /> Add Source
          </Button>
        </form>
      </Panel>
    </div>
  );
};

export default SourcesSection;
